// =====================================================
// 대출/반납 관리 탭
// AdminLibraryManagement.tsx 안의 LoanTab 함수를 이 내용으로 교체
// =====================================================
import { useState, useEffect } from 'react'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface LoanListItem {
    loan_id: number
    member_id: number
    member_name: string | null
    book_title: string
    barcode: string
    loaned_at: string
    returned_at: string | null
    status: string // "loaned" | "returned"
}

interface LoanDetail {
    loan_id: number
    loaned_at: string
    returned_at: string | null
    status: string
    member: {
        id: number
        name: string | null
        email: string | null
    }
    book: {
        id: number
        barcode: string
        book_code: string | null
        title: string
        author: string | null
    }
}

function LoanTab() {
    const [loans, setLoans] = useState<LoanListItem[]>([])
    const [loading, setLoading] = useState(true)
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState<'' | 'loaned' | 'returned'>('')
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(0)
    const limit = 20

    const [detailModal, setDetailModal] = useState<{ open: boolean; loanId: number | null }>({ open: false, loanId: null })

    const fetchLoans = async () => {
        setLoading(true)
        try {
            const params = new URLSearchParams({ page: String(page), limit: String(limit) })
            if (statusFilter) params.set('status', statusFilter)
            const res = await fetchWithAuth(getApiUrl(`/marubit/api/book/admin/loans?${params.toString()}`))
            if (!res.ok) throw new Error('조회 실패')
            const json = await res.json()
            setLoans(json.data.loans)
            setTotal(json.data.total)
        } catch {
            // noop
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchLoans() }, [page, statusFilter])

    const handleSearch = () => {
        setPage(1)
        setSearch(searchInput.trim())
    }

    const filtered = loans.filter(loan => {
        if (!search) return true
        const s = search.toLowerCase()
        return (
            loan.book_title.toLowerCase().includes(s) ||
            loan.barcode.toLowerCase().includes(s) ||
            (loan.member_name ?? '').toLowerCase().includes(s)
        )
    })

    const totalPages = Math.max(1, Math.ceil(total / limit))
    const formatDateTime = (dateStr: string) =>
        new Date(dateStr).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })

    return (
        <>
            {/* 대출 가능 권수 설정 */}
            <LoanSettingCard />

            {/* 검색 + 필터 */}
            <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                <div className="flex items-center gap-2 flex-wrap">
                    <select
                        value={statusFilter}
                        onChange={e => { setPage(1); setStatusFilter(e.target.value as any) }}
                        className="h-9 px-3 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267] bg-white"
                    >
                        <option value="">전체</option>
                        <option value="loaned">대출중</option>
                        <option value="returned">반납완료</option>
                    </select>
                    <input
                        type="text"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                        placeholder="도서코드, 도서명, 대출자명 검색"
                        className="w-72 h-9 px-3 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]"
                    />
                    <button onClick={handleSearch} className="h-9 px-5 bg-[#887267] text-white rounded-lg text-[13px] font-semibold hover:bg-[#6b5248] transition-colors">
                        조회
                    </button>
                </div>
            </div>

            {/* 테이블 */}
            <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                <table className="w-full">
                    <thead>
                        <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                            <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">바코드</th>
                            <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">도서명</th>
                            <th className="text-center px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">구분</th>
                            <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">대출자</th>
                            <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[160px]">대출/반납일</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="text-center py-12 text-gray-400 text-[14px]">불러오는 중...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={5} className="text-center py-12 text-gray-300 text-[13px]">대출/반납 내역이 없습니다.</td></tr>
                        ) : filtered.map(loan => (
                            <tr key={loan.loan_id} className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                                onClick={() => setDetailModal({ open: true, loanId: loan.loan_id })}>
                                <td className="px-4 py-3 text-[12px] text-gray-400 font-mono">{loan.barcode}</td>
                                <td className="px-4 py-3 text-[13px] font-medium text-[#2a1f1a]">{loan.book_title}</td>
                                <td className="px-4 py-3 text-center">
                                    <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${loan.status === 'loaned' ? 'bg-orange-50 text-orange-500' : 'bg-green-50 text-green-600'}`}>
                                        {loan.status === 'loaned' ? '대출' : '반납'}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-[12px] text-gray-500">{loan.member_name ?? '-'}</td>
                                <td className="px-4 py-3 text-[12px] text-gray-400 whitespace-nowrap">
                                    {formatDateTime(loan.status === 'loaned' ? loan.loaned_at : (loan.returned_at ?? loan.loaned_at))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>


            {/* 페이지네이션 */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-5">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                        className="px-3 h-8 border border-[#e8e0dc] rounded-lg text-[12px] text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#f5f2f0]">
                        이전
                    </button>
                    <span className="text-[13px] text-gray-500">{page} / {totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                        className="px-3 h-8 border border-[#e8e0dc] rounded-lg text-[12px] text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#f5f2f0]">
                        다음
                    </button>
                </div>
            )}

            {/* 상세 모달 */}
            {detailModal.open && detailModal.loanId && (
                <LoanDetailModal loanId={detailModal.loanId} onClose={() => setDetailModal({ open: false, loanId: null })} />
            )}
        </>
    )
}

// ===================== 대출 가능 권수 설정 카드 =====================
function LoanSettingCard() {
    const [maxLoans, setMaxLoans] = useState<number | null>(null)
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null)

    useEffect(() => {
        const fetchSetting = async () => {
            try {
                const res = await fetchWithAuth(getApiUrl('/marubit/api/book/admin/loan-setting'))
                if (!res.ok) throw new Error()
                const json = await res.json()
                const value = json.data?.max_loans_per_member ?? null
                setMaxLoans(value)
                setInput(value != null ? String(value) : '')
            } catch {
                // noop
            } finally {
                setLoading(false)
            }
        }
        fetchSetting()
    }, [])

    const handleSave = async () => {
        const trimmed = input.trim()
        const num = Number(trimmed)
        if (!trimmed || !Number.isInteger(num) || num < 1) {
            setMsg({ text: '1 이상의 정수를 입력해주세요.', ok: false })
            return
        }
        setSaving(true)
        setMsg(null)
        try {
            const res = await fetchWithAuth(getApiUrl('/marubit/api/book/admin/loan-setting'), {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ max_loans_per_member: num }),
            })
            const json = await res.json()
            if (!res.ok) throw new Error(json?.error?.message || '저장 실패')
            setMaxLoans(json.data?.max_loans_per_member ?? num)
            setMsg({ text: '저장되었습니다.', ok: true })
        } catch (e: any) {
            setMsg({ text: e.message ?? '저장에 실패했습니다.', ok: false })
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <p className="m-0 text-[13px] font-bold text-[#5a3d32]">1인당 대출 가능 권수</p>
                    <p className="m-0 mt-0.5 text-[12px] text-gray-400">
                        {loading
                            ? '불러오는 중...'
                            : maxLoans != null
                                ? `현재 회원 1인당 최대 ${maxLoans}권까지 동시 대출할 수 있습니다.`
                                : '현재 제한이 설정되어 있지 않습니다. (무제한)'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="number"
                        min={1}
                        value={input}
                        onChange={e => { setInput(e.target.value); setMsg(null) }}
                        onKeyDown={e => e.key === 'Enter' && handleSave()}
                        placeholder="권수"
                        className="w-20 h-9 px-3 border border-[#e8e0dc] rounded-lg text-[13px] text-center outline-none focus:border-[#887267]"
                    />
                    <span className="text-[13px] text-gray-500">권</span>
                    <button
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="h-9 px-4 bg-[#887267] text-white rounded-lg text-[13px] font-semibold hover:bg-[#6b5248] transition-colors disabled:opacity-50"
                    >
                        {saving ? '저장 중...' : '저장'}
                    </button>
                </div>
            </div>
            {msg && (
                <p className={`m-0 mt-2 text-[12px] ${msg.ok ? 'text-green-600' : 'text-red-400'}`}>{msg.text}</p>
            )}
        </div>
    )
}

// ===================== 대출/반납 상세 모달 =====================
function LoanDetailModal({ loanId, onClose }: { loanId: number; onClose: () => void }) {
    const [detail, setDetail] = useState<LoanDetail | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchDetail = async () => {
            try {
                const res = await fetchWithAuth(getApiUrl(`/marubit/api/book/admin/loans/${loanId}`))
                if (!res.ok) throw new Error('조회 실패')
                const json = await res.json()
                setDetail(json.data)
            } catch {
                // noop
            } finally {
                setLoading(false)
            }
        }
        fetchDetail()
    }, [loanId])

    const formatDateTime = (dateStr: string) =>
        new Date(dateStr).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[440px] mx-4 p-6">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-[17px] font-bold text-[#2a1f1a]">대출/반납 상세</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-gray-400 text-[13px]">불러오는 중...</div>
                ) : !detail ? (
                    <div className="py-10 text-center text-gray-300 text-[13px]">정보를 불러올 수 없습니다.</div>
                ) : (
                    <div className="flex flex-col gap-4">
                        <div className="bg-[#f5f2f0] rounded-xl p-4">
                            <p className="text-[12px] text-gray-400 mb-1">도서 정보</p>
                            <p className="text-[14px] font-semibold text-[#2a1f1a]">{detail.book.title}</p>
                            <p className="text-[12px] text-gray-500 mt-1">
                                도서코드 {detail.book.book_code ?? '-'} · 바코드 {detail.book.barcode}
                                {detail.book.author && ` · 저자 ${detail.book.author}`}
                            </p>
                        </div>

                        <div className="bg-[#f5f2f0] rounded-xl p-4">
                            <p className="text-[12px] text-gray-400 mb-1">대출자 정보</p>
                            <p className="text-[14px] font-semibold text-[#2a1f1a]">{detail.member.name ?? '알 수 없음'}</p>
                            <p className="text-[12px] text-gray-500 mt-1">{detail.member.email ?? '-'}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-[12px] text-gray-400 mb-1">구분</p>
                                <span className={`inline-block text-[12px] px-2 py-1 rounded font-medium ${detail.status === 'loaned' ? 'bg-orange-50 text-orange-500' : 'bg-green-50 text-green-600'}`}>
                                    {detail.status === 'loaned' ? '대출중' : '반납완료'}
                                </span>
                            </div>
                            <div>
                                <p className="text-[12px] text-gray-400 mb-1">대출일</p>
                                <p className="text-[13px] text-[#2a1f1a]">{formatDateTime(detail.loaned_at)}</p>
                            </div>
                        </div>

                        {detail.returned_at && (
                            <div>
                                <p className="text-[12px] text-gray-400 mb-1">반납일</p>
                                <p className="text-[13px] text-[#2a1f1a]">{formatDateTime(detail.returned_at)}</p>
                            </div>
                        )}
                    </div>
                )}

                <button onClick={onClose} className="w-full mt-6 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold">
                    닫기
                </button>
            </div>
        </div>
    )
}

export default LoanTab